import {
  TableHead,
  TableRow,
  TableCell,
  SortDirection,
  Stack,
  SxProps,
  Theme,
} from '@mui/material';
import { ReactNode } from 'react';
import { Caret, CaretPlaceholder } from '../Caret';
import { AlignedElement, DataTableHeader, LastColumnElement } from './types';
import { CELL_HORIZONTAL_PADDING, ELLIPSIS_COLUMN_WIDTH, LEADING_COLUMN_WIDTH } from './DataTable';

interface DataTableHeaderProps {
  headers: DataTableHeader[];
  borderOverride?: SxProps<Theme>;
  leftElement?: ReactNode;
  rightElement?: ReactNode | AlignedElement;
  sortByCol: string;
  sortDirection: SortDirection;
  hideCaret?: boolean;
  outlined?: boolean;
  hasLeadingColumn: boolean;
  lastColumnElement?: LastColumnElement | ((row: any) => LastColumnElement);
  flexLayout?: boolean;
  isNested?: boolean;
  onSort: (header: DataTableHeader) => void;
}

export const TableHeader = ({
  headers,
  borderOverride,
  leftElement,
  rightElement,
  sortByCol,
  sortDirection,
  hideCaret,
  outlined,
  hasLeadingColumn,
  flexLayout,
  lastColumnElement = 'none',
  isNested = false,
  onSort,
}: DataTableHeaderProps) => {
  const showHeaders = !isNested;
  const totalCols =
    headers.length + (lastColumnElement !== 'none' ? 1 : 0) + (hasLeadingColumn ? 1 : 0);
  const alignWithColumnsEnabled =
    rightElement &&
    typeof rightElement === 'object' &&
    'alignWithColumns' in rightElement &&
    rightElement.alignWithColumns === true;
  const lastColumnWidth =
    lastColumnElement === 'menu' ? ELLIPSIS_COLUMN_WIDTH : LEADING_COLUMN_WIDTH;

  return (
    <TableHead
      sx={
        borderOverride
          ? {
              '& .MuiTableCell-head': {
                borderBottom: 'none',
              },
            }
          : undefined
      }>
      {showHeaders && (leftElement || rightElement) && !alignWithColumnsEnabled && (
        <TableRow>
          <TableCell colSpan={totalCols} sx={{ border: 'none' }}>
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
              {leftElement}
              {rightElement as ReactNode}
            </Stack>
          </TableCell>
        </TableRow>
      )}

      {showHeaders && alignWithColumnsEnabled && (
        <TableRow
          sx={{
            ...(flexLayout && {
              display: 'flex',
              width: '100%',
            }),
          }}>
          {hasLeadingColumn && (
            <TableCell
              sx={{
                width: LEADING_COLUMN_WIDTH,
                maxWidth: LEADING_COLUMN_WIDTH,
                minWidth: LEADING_COLUMN_WIDTH,
                flex: 'none',
                border: 'none',
                whiteSpace: 'nowrap',
                wordBreak: 'break-word',
                overflow: 'visible',
              }}>
              {leftElement}
            </TableCell>
          )}
          {headers.map((header, index) => (
            <TableCell
              key={header.label}
              align={header.align}
              sx={{
                ...(flexLayout && header.flex !== undefined && { flex: header.flex }),
                overflow: 'visible',
                wordBreak: 'break-word',
                minWidth: 0,
                border: 'none',
                ...(header.align === 'right' &&
                  index !== headers.length - 1 && {
                    paddingRight: CELL_HORIZONTAL_PADDING,
                  }),
              }}>
              {(rightElement as AlignedElement).values[header.label]}
            </TableCell>
          ))}
          {lastColumnElement === 'spacer' && !outlined ? (
            <TableCell
              sx={{
                width: LEADING_COLUMN_WIDTH,
                maxWidth: LEADING_COLUMN_WIDTH,
                minWidth: LEADING_COLUMN_WIDTH,
                flex: 'none',
                border: 'none',
                padding: 0,
              }}
            />
          ) : lastColumnElement !== 'none' && !outlined ? (
            <TableCell
              sx={{
                border: 'none',
                ...(flexLayout && {
                  width: lastColumnWidth,
                  maxWidth: lastColumnWidth,
                  minWidth: lastColumnWidth,
                  flex: 'none',
                }),
              }}>
              &nbsp;
            </TableCell>
          ) : null}
        </TableRow>
      )}

      {showHeaders ? (
        <TableRow
          sx={{
            ...(flexLayout && {
              display: 'flex',
              width: '100%',
            }),
            alignItems: 'flex-end',
          }}>
          {hasLeadingColumn && (
            <TableCell
              sx={{
                width: LEADING_COLUMN_WIDTH,
                maxWidth: LEADING_COLUMN_WIDTH,
                minWidth: LEADING_COLUMN_WIDTH,
                flex: 'none',
              }}
            />
          )}
          {headers.map((header, index) => {
            const isRightAligned = header.align === 'right';
            const isLastHeader = index === headers.length - 1;
            const isSortable = header.sortable ?? true;
            const caret =
              isSortable &&
              !hideCaret &&
              (sortByCol === header.label ? (
                <Caret sortDirection={sortDirection === 'asc'} />
              ) : (
                <CaretPlaceholder />
              ));
            return (
              <TableCell
                key={header.label}
                className={outlined ? 'paddedHeader' : undefined}
                align={header.align}
                sx={[
                  {
                    ...(flexLayout && header.flex !== undefined && { flex: header.flex }),
                    userSelect: 'none',
                    ...(isSortable && { cursor: 'pointer' }),
                    overflow: 'visible',
                    minWidth: 0,
                    ...(header.align === 'right' &&
                      !isLastHeader && {
                        paddingRight: CELL_HORIZONTAL_PADDING,
                      }),
                  },
                  ...(Array.isArray(header.cellSx) ? header.cellSx : [header.cellSx]),
                ]}
                onClick={isSortable ? () => onSort(header) : undefined}>
                <Stack
                  direction='row'
                  spacing={1}
                  alignItems='center'
                  justifyContent={isRightAligned ? 'flex-end' : 'flex-start'}
                  width='100%'>
                  {isRightAligned ? (
                    <>
                      {caret}
                      {header.title}
                    </>
                  ) : (
                    <>
                      {header.title}
                      {caret}
                    </>
                  )}
                </Stack>
              </TableCell>
            );
          })}

          {lastColumnElement === 'spacer' && !outlined ? (
            <TableCell
              sx={{
                width: LEADING_COLUMN_WIDTH,
                maxWidth: LEADING_COLUMN_WIDTH,
                minWidth: LEADING_COLUMN_WIDTH,
                flex: 'none',
                padding: 0,
              }}
            />
          ) : lastColumnElement !== 'none' && !outlined ? (
            <TableCell
              sx={{
                ...(flexLayout && {
                  width: lastColumnWidth,
                  maxWidth: lastColumnWidth,
                  minWidth: lastColumnWidth,
                  flex: 'none',
                }),
              }}>
              &nbsp;
            </TableCell>
          ) : null}
        </TableRow>
      ) : null}
    </TableHead>
  );
};
